import {
  PlaceholderPreviewBase,
  PlaceholderPreviewBaseProps,
} from "@/components/r3f/utils/placeholder-preview-base";
import { useImgPreviewSignedUrl } from "@/hooks/use-img-preview-signed-url";
import { useCached3DObjectIfExists } from "@/object-cache";
import { useAppSelector } from "@/store/store-hooks";
import { GUID, isLoading } from "@faro-lotv/foundation";
import { isRevisionScanEntity } from "@faro-lotv/service-wires";
import { useMemo, type JSX } from "react";
import { useLazyLoadRevisionImg360 } from "../loading/use-load-revision-img360s";
import { selectRevisionEntity } from "../store/revision/revision-entity-selectors";
import { selectPointCloudStreamForScanEntity } from "../store/revision/revision-scan-selectors";

/**
 * Delay in milliseconds before the preview popover is hidden after the pointer
 * leaves both the anchor and the preview. This bridges the gap so the user can
 * move the mouse onto the preview to interact with the panorama viewer.
 */
export const PREVIEW_CLOSE_DELAY_MS = 250;

type ScanPreviewProps = Pick<
  PlaceholderPreviewBaseProps,
  | "anchorEl"
  | "placement"
  | "disablePortal"
  | "isVisible"
  | "popperModifiers"
  | "onPreviewHoverChange"
> & {
  /** The ID of the scan to show the preview for. */
  scanId: GUID;
};

/**
 * @returns A preview popover for the scan, showing details such as name and an interactive
 * 360-degree panorama viewer. Shows nothing if the scan is not found.
 */
export function ScanPreview({
  scanId,
  isVisible = false,
  anchorEl,
  onPreviewHoverChange,
  ...previewProps
}: ScanPreviewProps): JSX.Element | null {
  const entity = useAppSelector(selectRevisionEntity(scanId));
  const scanEntity = useMemo(
    () => (entity && isRevisionScanEntity(entity) ? entity : undefined),
    [entity],
  );

  // When hovering, load the pano image captured by the scanner to show as a preview
  const img360 = useLazyLoadRevisionImg360(scanEntity, isVisible);

  const previewSignedUrl = useImgPreviewSignedUrl(img360);

  // Load the pano object here (outside the popover) because the popover portal breaks React context
  const panoObject = useCached3DObjectIfExists(
    isLoading(img360) ? undefined : img360,
    "ScanPreview",
  );
  const isDisabledScan = !useAppSelector(
    selectPointCloudStreamForScanEntity(scanEntity),
  );

  if (!isVisible || !scanEntity || !anchorEl) {
    return null;
  }

  const disabledReason = isDisabledScan
    ? "This point cloud is currently unavailable and cannot be displayed"
    : undefined;

  return (
    <PlaceholderPreviewBase
      name={scanEntity.name}
      createdAt={scanEntity.createdAt}
      imageUri={previewSignedUrl}
      panoObject={panoObject}
      disabledReason={disabledReason}
      anchorEl={anchorEl}
      isVisible={isVisible}
      onPreviewHoverChange={onPreviewHoverChange}
      {...previewProps}
    />
  );
}
